/**
 * ComparisonHandle - The draggable handle for the comparison slider
 *
 * Displays as a vertical line (horizontal mode) or horizontal line (vertical mode)
 * with a circular grip area in the center.
 *
 * Feature: 081-photo-versioning (T054)
 */

import { cn } from '@/lib/utils';

export interface ComparisonHandleProps {
  /** Position of the handle (0-100) */
  position: number;
  /** Orientation of the slider */
  orientation?: 'horizontal' | 'vertical';
  /** Whether the handle is currently being dragged */
  isDragging?: boolean;
  /** Handler for pointer down events */
  onPointerDown?: (e: React.PointerEvent) => void;
  /** Handler for keyboard events */
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** ARIA label for accessibility */
  ariaLabel?: string;
}

/**
 * Draggable handle for image comparison slider
 */
export function ComparisonHandle({
  position,
  orientation = 'horizontal',
  isDragging = false,
  onPointerDown,
  onKeyDown,
  ariaLabel = 'Comparison slider',
}: ComparisonHandleProps) {
  const isHorizontal = orientation === 'horizontal';

  return (
    <div
      className={cn(
        'absolute z-10 flex items-center justify-center',
        // Position based on orientation
        isHorizontal
          ? 'top-0 bottom-0 w-[2px] -translate-x-1/2 cursor-ew-resize'
          : 'left-0 right-0 h-[2px] -translate-y-1/2 cursor-ns-resize',
        // Drag state styling
        isDragging && 'opacity-90'
      )}
      style={{
        [isHorizontal ? 'left' : 'top']: `${position}%`,
      }}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      role="slider"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(position)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
    >
      {/* The line */}
      <div
        className={cn(
          'absolute bg-white shadow-md',
          isHorizontal
            ? 'w-[2px] h-full'
            : 'h-[2px] w-full'
        )}
      />

      {/* The grip circle */}
      <div
        className={cn(
          'relative z-20 flex items-center justify-center',
          'w-10 h-10 rounded-full',
          'bg-white shadow-lg border-2 border-gray-200',
          'transition-transform duration-100',
          isDragging && 'scale-110'
        )}
      >
        {/* Grip lines */}
        <div className="flex gap-1">
          {isHorizontal ? (
            <>
              <div className="w-0.5 h-4 bg-gray-400 rounded-full" />
              <div className="w-0.5 h-4 bg-gray-400 rounded-full" />
            </>
          ) : (
            <>
              <div className="w-4 h-0.5 bg-gray-400 rounded-full" />
              <div className="w-4 h-0.5 bg-gray-400 rounded-full" />
            </>
          )}
        </div>
      </div>

      {/* Hidden range input for accessibility (T056) */}
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(position)}
        onChange={() => {}}
        className="sr-only"
        aria-hidden="true"
      />
    </div>
  );
}
